/**
 * Search Service for Hilton Analytica COA Platform
 * 
 * Provides COA document search functionality with pagination support.
 * Handles case-insensitive partial matching via the backend Lambda.
 */

import {
  SearchQuery,
  SearchResponse,
  LambdaErrorResponse,
  RequestOptions,
} from '../types';
import { BaseApiService, ApiServiceError } from './baseApiService';

/**
 * Configuration for the search Lambda endpoint
 * In production, this would be set from environment variables
 */
const SEARCH_ENDPOINT = '/search';
const SEARCH_TIMEOUT = 30000; // 30 seconds as per requirement 3.3
const DEFAULT_PAGE_SIZE = 20; // Default 20 records per page as per requirement 3.6

/**
 * Extended request options for search operations
 */
interface SearchRequestOptions extends RequestOptions {
  timeout?: number;
  retries?: number;
}

/**
 * Default options for search requests
 */
const DEFAULT_SEARCH_OPTIONS: SearchRequestOptions = {
  timeout: SEARCH_TIMEOUT,
  retries: 3,
  retryDelay: 1000,
};

/**
 * Search service class for COA document search
 */
export class SearchService {
  private apiService: BaseApiService;

  constructor(apiService?: BaseApiService) {
    // Initialize with default search endpoint configuration
    this.apiService = apiService || new BaseApiService('', DEFAULT_SEARCH_OPTIONS);
  }

  /**
   * Search for COA documents
   * 
   * @param query - Search parameters including productName, lotNumber, date range, and pagination
   * @returns Promise resolving to SearchResponse with results, total count, and pagination info
   * @throws ApiServiceError if the search fails after all retry attempts
   * 
   * Features:
   * - Case-insensitive partial matching (handled by backend per requirement 3.5)
   * - Pagination with default pageSize of 20 (requirement 3.6)
   * - 30 second timeout (requirement 3.3)
   * - Retry logic up to 3 attempts
   */
  async searchCOA(query: SearchQuery): Promise<SearchResponse> {
    // Set default pageSize to 20 if not provided
    const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE;
    const page = query.page ?? 1;

    // Build search parameters
    const searchParams: Record<string, string | number | undefined> = {
      page,
      pageSize,
    };

    // Add optional search parameters
    if (query.productName?.trim()) {
      searchParams.productName = query.productName.trim();
    }

    if (query.lotNumber?.trim()) {
      searchParams.lotNumber = query.lotNumber.trim();
    }

    if (query.startDate) {
      searchParams.startDate = query.startDate;
    }

    if (query.endDate) {
      searchParams.endDate = query.endDate;
    }

    try {
      // Make GET request to search Lambda endpoint
      const response = await this.apiService.get<SearchResponse>(
        SEARCH_ENDPOINT,
        searchParams,
        DEFAULT_SEARCH_OPTIONS
      );

      // Handle successful response
      if (response.success) {
        return response.data;
      }

      // Handle error response from Lambda
      const errorResponse = response as LambdaErrorResponse;
      throw new ApiServiceError(
        errorResponse.error || 'Search failed',
        500,
        errorResponse.code
      );
    } catch (error) {
      if (error instanceof ApiServiceError) {
        throw error;
      }
      throw new ApiServiceError(
        error instanceof Error ? error.message : 'Unknown error during search',
        undefined,
        'SEARCH_ERROR'
      );
    }
  }

  /**
   * Search for COA documents using POST method
   * 
   * @param query - Search parameters including productName, lotNumber, date range, and pagination
   * @returns Promise resolving to SearchResponse with results, total count, and pagination info
   * @throws ApiServiceError if the search fails after all retry attempts
   */
  async searchCOAPost(query: SearchQuery): Promise<SearchResponse> {
    // Set default pageSize to 20 if not provided
    const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE;
    const page = query.page ?? 1;

    // Build search body
    const searchBody: Record<string, string | number | undefined> = {
      page,
      pageSize,
    };

    // Add optional search parameters
    if (query.productName?.trim()) {
      searchBody.productName = query.productName.trim();
    }

    if (query.lotNumber?.trim()) {
      searchBody.lotNumber = query.lotNumber.trim();
    }

    if (query.startDate) {
      searchBody.startDate = query.startDate;
    }

    if (query.endDate) {
      searchBody.endDate = query.endDate;
    }

    try {
      // Make POST request to search Lambda endpoint
      const response = await this.apiService.post<SearchResponse>(
        SEARCH_ENDPOINT,
        searchBody,
        DEFAULT_SEARCH_OPTIONS
      );

      // Handle successful response
      if (response.success) {
        return response.data;
      }

      // Handle error response from Lambda
      const errorResponse = response as LambdaErrorResponse;
      throw new ApiServiceError(
        errorResponse.error || 'Search failed',
        500,
        errorResponse.code
      );
    } catch (error) {
      if (error instanceof ApiServiceError) {
        throw error;
      }
      throw new ApiServiceError(
        error instanceof Error ? error.message : 'Unknown error during search',
        undefined,
        'SEARCH_ERROR'
      );
    }
  }

  /**
   * Get the default page size
   */
  getDefaultPageSize(): number {
    return DEFAULT_PAGE_SIZE;
  }

  /**
   * Calculate total pages from total results and page size
   */
  calculateTotalPages(total: number, pageSize?: number): number {
    const size = pageSize ?? DEFAULT_PAGE_SIZE;
    return Math.ceil(total / size);
  }
}

// Export default instance
export const searchService = new SearchService();

// Export individual function for convenience
export async function searchCOA(query: SearchQuery): Promise<SearchResponse> {
  return searchService.searchCOA(query);
}